export type SpotifyArtistInputResult =
  | { ok: true; artistId: string }
  | { ok: false; reason: 'not-url' | 'not-artist' | 'invalid' };

const SPOTIFY_HOSTS = new Set(['open.spotify.com', 'www.open.spotify.com']);
const ARTIST_ID = /^[A-Za-z0-9]+$/;
const LOCALE_SEGMENT = /^intl-[A-Za-z0-9-]+$/i;

/** UI may expose either `Spotify` or `Spotify | Artists` for the same artist flow. */
export function shouldNormalizeSpotifyArtistType(typeName: string | null | undefined): boolean {
  const name = (typeName ?? '').trim().toLowerCase();
  return name === 'spotify' || name === 'spotify | artists';
}

export function isStrictSpotifyArtistsType(typeName: string | null | undefined): boolean {
  return (typeName ?? '').trim().toLowerCase() === 'spotify | artists';
}

export function normalizeSpotifyArtistInput(raw: string): SpotifyArtistInputResult {
  const value = raw.trim();
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) {
    return { ok: false, reason: 'not-url' };
  }

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return { ok: false, reason: 'invalid' };
  }

  const host = url.hostname.toLowerCase();
  if (!SPOTIFY_HOSTS.has(host) || url.protocol !== 'https:') {
    return { ok: false, reason: 'invalid' };
  }

  const segments = url.pathname.split('/').filter(Boolean);
  const artistIndex = segments[0]?.toLowerCase() === 'artist'
    ? 0
    : LOCALE_SEGMENT.test(segments[0] || '') && segments[1]?.toLowerCase() === 'artist'
      ? 1
      : -1;

  if (artistIndex < 0) {
    return { ok: false, reason: 'not-artist' };
  }

  const artistId = segments[artistIndex + 1];
  if (segments.length !== artistIndex + 2 || !artistId || !ARTIST_ID.test(artistId)) {
    return { ok: false, reason: 'invalid' };
  }

  return { ok: true, artistId };
}
